import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeType = 'default-custom-theme' | 'switz-custom-theme' | 'rfetm-custom-theme' | 'nbtf-custom-theme' | 'ptta-custom-theme' | 'ttnz-custom-theme' | 'sbtf-custom-theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeServiceService {
  private readonly renderer: Renderer2;
  private readonly isDarkSubject = new BehaviorSubject<boolean>(false);
  private readonly currentThemeSubject = new BehaviorSubject<ThemeType>('default-custom-theme');
  private readonly darkModeStorageKey = 'theme-dark';
  public isDark$: Observable<boolean> = this.isDarkSubject.asObservable();
  public currentTheme$: Observable<ThemeType> = this.currentThemeSubject.asObservable();
  public isDark = false;
  private currentTheme: ThemeType = 'default-custom-theme';
  private readonly availableThemes: ThemeType[] = ['default-custom-theme', 'switz-custom-theme', 'rfetm-custom-theme', 'nbtf-custom-theme', 'ptta-custom-theme','ttnz-custom-theme', 'sbtf-custom-theme'];

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.initializeTheme();
  }

  private initializeTheme(): void {
    const savedTheme = localStorage.getItem('theme');
    const savedDark = localStorage.getItem(this.darkModeStorageKey);

    this.isDark = savedDark === 'true' || savedTheme === 'dark';
    if (this.isDark) {
      this.renderer.addClass(document.body, 'dark-theme');
    } else {
      this.renderer.removeClass(document.body, 'dark-theme');
    }

    this.currentTheme = this.availableThemes.includes(savedTheme as ThemeType)
      ? (savedTheme as ThemeType)
      : 'default-custom-theme';
    this.applyTheme(this.currentTheme);
    this.isDarkSubject.next(this.isDark);
    this.currentThemeSubject.next(this.currentTheme);
  }

  private removeAllThemeClasses(): void {
    this.availableThemes.forEach(theme => {
      this.renderer.removeClass(document.body, theme);
    });
  }

  private applyTheme(theme: ThemeType): void {
    this.removeAllThemeClasses();
    // Only add theme class if it's not the default theme
    // Default theme is applied when no theme class is present
    if (theme !== 'default-custom-theme') {
      this.renderer.addClass(document.body, theme);
    }
    this.currentTheme = theme;
    localStorage.setItem('theme', theme);
    this.currentThemeSubject.next(this.currentTheme);
  }

  setTheme(theme: ThemeType): void {
    this.applyTheme(theme);
  }

  toggleTheme(): void {
    this.isDark = !this.isDark;
    if (this.isDark) {
      this.enableDarkTheme();
    } else {
      this.disableDarkTheme();
    }
    this.isDarkSubject.next(this.isDark);
  }

  getTheme(): boolean {
    return this.isDark;
  }

  getCurrentTheme(): ThemeType {
    return this.currentTheme;
  }

  getAvailableThemes(): ThemeType[] {
    return [...this.availableThemes];
  }

  enableDarkTheme(): void {
    this.renderer.addClass(document.body, 'dark-theme');
    localStorage.setItem(this.darkModeStorageKey, 'true');
    this.isDark = true;
    this.isDarkSubject.next(this.isDark);
  }

  disableDarkTheme(): void {
    this.renderer.removeClass(document.body, 'dark-theme');
    localStorage.setItem(this.darkModeStorageKey, 'false');
    this.isDark = false;
    this.isDarkSubject.next(this.isDark);
  }

  /**
   * Apply dark/light without persisting to localStorage.
   * Useful when the app is embedded in an iframe and the parent controls theme.
   */
  setDarkModeFromIframe(theme: 'dark' | 'light'): void {
    const shouldBeDark = theme === 'dark';
    if (shouldBeDark) {
      this.renderer.addClass(document.body, 'dark-theme');
    } else {
      this.renderer.removeClass(document.body, 'dark-theme');
    }
    this.isDark = shouldBeDark;
    this.isDarkSubject.next(this.isDark);
  }

  setCustomTheme(theme: string): void {
    if (this.availableThemes.includes(theme as ThemeType)) {
      this.applyTheme(theme as ThemeType);
      return;
    }
    this.applyTheme('default-custom-theme');
  }
}
